import {
	action,
	autorun,
	observable,
	makeObservable,
	computed,
	reaction,
	runInAction,
} from 'mobx';
import {enumToValues} from '@netx/core-next/utils/enum';
import {getUserLabel} from '@netx/core-user-next/api/getUserLabel';
import {RuleModel} from '../baseModel';
import {
	CheckoutType,
	SearchTypeCheckouts,
	TSearchTypeCheckouts,
} from './constants';
import {checkoutsRuleDefaults} from './defaults';
import type {CheckoutsRule} from './types';
import {validateCheckoutsRule} from './validate';

export class CheckoutsRuleModel
	extends RuleModel<TSearchTypeCheckouts>
	implements Omit<CheckoutsRule, 'elementValue1'>
{
	readonly elementType = SearchTypeCheckouts;
	elementSubType1: CheckoutType = checkoutsRuleDefaults.elementSubType1;
	elementSubType2: 0 = checkoutsRuleDefaults.elementSubType2;
	elementValue1: number = checkoutsRuleDefaults.elementValue1;
	elementValue2: '' = checkoutsRuleDefaults.elementValue2;
	elementValue3: '' = checkoutsRuleDefaults.elementValue3;

	get elementSubType1Options() {
		return enumToValues(CheckoutType);
	}

	get isValid() {
		const validation = validateCheckoutsRule(this as CheckoutsRule);
		this.setValidationErrors(this.dirty && validation ? validation : undefined);
		if (!validation) return true;
		return false;
	}

	constructor(params?: Partial<Omit<CheckoutsRule, 'elementType'>>) {
		super();

		runInAction(() => {
			Object.assign(this, {...checkoutsRuleDefaults, ...params});
		});

		makeObservable(this, {
			elementSubType1: observable,
			elementSubType1Options: computed,
			elementValue1: observable,
			isValid: computed,
			setElementSubType1: action,
			setElementValue1: action,
		});

		this.reactionDisposers.push(
			reaction(
				() => this.elementSubType1,
				(elementSubType1) => {
					if (elementSubType1 === CheckoutType.ALL) {
						this.elementValue1 = 0;
						this.setElementValue1Label('');
						this.setElementValue1Loading(false);
						this.dirty = false;
					}
				},
			),
			autorun(() => {
				this.setElementValue1Unavailable(false);

				if (!this.elementValue1) return;

				this.setElementValue1Loading(true);

				getUserLabel({id: this.elementValue1})
					.then((userLabel) => {
						this.setElementValue1Label(userLabel);
					})
					.catch(() => {
						this.setElementValue1Unavailable(true);
					})
					.finally(() => {
						this.setElementValue1Loading(false);
					});
			}),
		);
	}

	setElementSubType1(elementSubType1: CheckoutType) {
		this.elementSubType1 = elementSubType1;
	}

	/** stubbed */
	setElementSubType2() {}

	setElementValue1(elementValue1: number) {
		this.elementValue1 = elementValue1;
		this.dirty = true;
	}

	/** stubbed */
	setElementValue2() {}

	/** stubbed */
	setElementValue3() {}
}

export default CheckoutsRuleModel;
